import React from 'react';
import Plate from './Plate';
import IngredientPhoto from './IngredientPhoto';
import { X, Trash2, Edit, ExternalLink } from 'lucide-react';
import config from '../config';
import { PRICE_SOURCE_LABEL, priceOrigin, suggestedPrice } from '../lib/utils';

export const DISH_CATEGORIES = [
    { value: 'starter', label: 'Entrée' },
    { value: 'main', label: 'Plat' },
    { value: 'dessert', label: 'Dessert' },
    { value: 'side', label: 'Accompagnement' },
    { value: 'beverage', label: 'Boisson' },
    { value: 'other', label: 'Autre' }
];

export const eur = (value, digits = 2) => `${parseFloat(value || 0).toFixed(digits).replace('.', ',')} €`;
export const unitLabel = (unit) => (unit === 'unit' ? 'pièce' : unit);
export const qty = (value) => String(parseFloat(value)).replace('.', ',');
export const isFood = (dish) => !['beverage', 'other'].includes(dish.category);
export const categoryLabel = (value) => DISH_CATEGORIES.find(c => c.value === value)?.label || value || '';

/** One dish in detail: selling price, margin and where each ingredient's cost comes from. */
const DishSheet = ({ dish: d, targetFoodCost, onClose, onDelete, onEdit, onUsePrice }) => {
    const lines = d.dish_ingredients || [];
    const cost = parseFloat(d.effective_cost || 0);
    const recipeTotal = parseFloat(d.total_cost || 0);
    const priceExVat = parseFloat(d.selling_price_ex_vat || 0);
    const margin = parseFloat(d.profit_margin || 0);
    const hasRecipe = lines.length > 0;
    const marginColor = margin >= 60 ? 'text-good' : margin >= 40 ? 'text-warn' : 'text-bad';
    const unpricedLine = lines.some((li) => parseFloat(li.ingredient_unit_price ?? 0) === 0);
    const advised = unpricedLine || !isFood(d) ? null : suggestedPrice(cost, parseFloat(d.vat_rate), targetFoodCost);
    return (
        <div className="sheet-overlay" onClick={onClose}>
            <div className="sheet sheet-wide" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <Plate share={priceExVat > 0 && cost > 0 ? cost / priceExVat : null} size={56} />
                        <div>
                            <h2 className="text-2xl leading-tight text-foreground">{d.name}</h2>
                            <p className="text-xs text-muted-foreground">
                                {categoryLabel(d.category)}, TVA {parseFloat(d.vat_rate || 0)} %
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Fermer" className="p-2 hover:bg-accent rounded-lg">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-lg border border-border bg-muted/30 p-4 mb-4">
                    <div>
                        <p className="text-xs font-medium text-muted-foreground">Prix TVAC</p>
                        <p className="mt-1 text-base font-bold text-foreground">{parseFloat(d.selling_price) > 0 ? eur(d.selling_price) : 'à définir'}</p>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-muted-foreground">Prix HTVA</p>
                        <p className="mt-1 text-base font-bold text-foreground">{priceExVat > 0 ? eur(priceExVat) : '—'}</p>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-muted-foreground">Coût HTVA</p>
                        <p className="mt-1 text-base font-bold text-foreground">{cost > 0 ? eur(cost) : '—'}</p>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-muted-foreground">Marge</p>
                        <p className={`mt-1 text-base font-bold ${priceExVat > 0 && cost > 0 ? marginColor : 'text-muted-foreground'}`}>
                            {priceExVat > 0 && cost > 0 ? `${margin.toFixed(1).replace('.', ',')} %` : '—'}
                        </p>
                    </div>
                </div>

                {advised != null && (
                    <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-muted p-3 text-sm">
                        <span>Prix conseillé à {targetFoodCost} % de coût matière : <b className="price whitespace-nowrap">{eur(advised)} TVAC</b> <span className="whitespace-nowrap">({eur(advised / (1 + parseFloat(d.vat_rate || 0) / 100))} HTVA, TVA {parseFloat(d.vat_rate || 0)} %)</span></span>
                        {advised > parseFloat(d.selling_price) && (
                            <button type="button" onClick={() => onUsePrice(advised)} className="btn-outline !min-h-[36px]">Utiliser ce prix</button>
                        )}
                    </div>
                )}

                {/* Cost breakdown */}
                {d.description && <p className="text-sm text-muted-foreground mb-4 whitespace-pre-line">{d.description}</p>}

                {d.open_questions?.length > 0 && (
                    <div className="mb-4 rounded-xl bg-muted p-3">
                        <h3 className="text-sm text-bad mb-1">À demander à la cuisine</h3>
                        <ul className="space-y-1 text-sm text-foreground">
                            {d.open_questions.map((q) => <li key={q.id}>{q.text}</li>)}
                        </ul>
                    </div>
                )}

                <h3 className="font-medium text-foreground mb-2">Détail du coût</h3>
                {hasRecipe && (() => {
                    const shares = {};
                    lines.forEach((li) => {
                        const key = parseFloat(li.ingredient_unit_price ?? 0) === 0 ? 'Sans prix'
                            : li.ingredient_is_preparation ? 'Recette maison'
                            : PRICE_SOURCE_LABEL[li.ingredient_price_source] || 'Source inconnue';
                        shares[key] = (shares[key] || 0) + parseFloat(li.cost || 0);
                    });
                    const text = Object.entries(shares)
                        .filter(([key, value]) => value > 0 || key === 'Sans prix')
                        .sort((a, b) => b[1] - a[1])
                        .map(([key, value]) => (key === 'Sans prix' ? 'une ligne sans prix' : `${Math.round((value / (recipeTotal || 1)) * 100)} % ${key.toLowerCase()}`))
                        .join(', ');
                    return <p className="mb-2 text-xs text-muted-foreground">Origine des prix : {text}.</p>;
                })()}
                {(d.is_finished_product && !hasRecipe) ? (
                    <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-foreground flex items-center gap-2">
                        <span className="text-xs px-1.5 py-0.5 tag text-foreground">Coût fixe</span>
                        Coût fixe de {eur(cost)} (aucun article lié).
                    </div>
                ) : hasRecipe ? (
                    <div className="rounded-lg border border-border overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-xs text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-2 text-left">Ingrédient</th>
                                    <th className="px-3 py-2 text-right">Quantité</th>
                                    <th className="px-3 py-2 text-right">Coût</th>
                                    <th className="hidden sm:table-cell px-3 py-2 text-right">Part</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {lines.map((li) => {
                                    const lineCost = parseFloat(li.cost || 0);
                                    const pct = recipeTotal > 0 ? (lineCost / recipeTotal) * 100 : 0;
                                    const unpriced = parseFloat(li.ingredient_unit_price ?? 0) === 0;
                                    return (
                                        <tr key={li.id} className={unpriced ? 'bg-bad/5' : ''}>
                                            <td className="px-3 py-2">
                                                <div className="flex items-start gap-3">
                                                <IngredientPhoto className="ingredient-thumb" imageUrl={li.ingredient_image_url} emoji={li.ingredient_emoji || (li.ingredient_is_preparation ? '🥣' : '🥄')} />
                                                <div className="min-w-0">
                                                <div className="text-foreground flex flex-wrap items-center gap-1.5">{li.ingredient_name}
                                                    {li.ingredient_is_preparation && <span className="tag text-good">préparation</span>}
                                                    {unpriced && <span className="text-[11px] px-1 py-0.5 rounded-full bg-bad/10 text-bad whitespace-nowrap">à chiffrer</span>}
                                                </div>
                                                {!unpriced && (
                                                    <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1">
                                                        <span className={`tag !whitespace-normal ${li.ingredient_price_source === 'estimate' ? 'text-warn' : 'text-foreground'}`}>
                                                            {li.ingredient_is_preparation ? 'Recette maison' : priceOrigin(li.ingredient_price_source, li.ingredient_price_date)}
                                                        </span>
                                                        {li.ingredient_supplier_name && <span>{li.ingredient_supplier_name}</span>}
                                                        <span>· {eur(li.ingredient_unit_price)}/{unitLabel(li.ingredient_unit)}</span>
                                                        {li.ingredient_webshop_url && (
                                                            <a href={li.ingredient_webshop_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 font-semibold text-primary underline underline-offset-2">
                                                                <ExternalLink className="h-3 w-3" /> webshop
                                                            </a>
                                                        )}
                                                        {li.ingredient_last_invoice_odoo_id && config.odooUrl && (
                                                            <a
                                                                href={`${config.odooUrl}/odoo/vendor-bills/${li.ingredient_last_invoice_odoo_id}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="inline-flex items-center gap-0.5 text-primary hover:underline"
                                                                title="Ouvrir la facture dans Odoo"
                                                            >
                                                                <ExternalLink className="h-3 w-3" /> dernière facture
                                                            </a>
                                                        )}
                                                    </div>
                                                )}
                                                {li.ingredient_source_product_name && (
                                                    <div className="hidden sm:block text-[11px] text-muted-foreground italic break-words" title={li.ingredient_source_product_name}>
                                                        réf. {li.ingredient_source_product_name}
                                                        {li.ingredient_source_product_price != null && ` · ${eur(li.ingredient_source_product_price)}`}
                                                    </div>
                                                )}
                                                </div>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-right text-muted-foreground whitespace-nowrap">{qty(li.quantity)} {unitLabel(li.unit)}</td>
                                            <td className={`px-3 py-2 text-right whitespace-nowrap ${unpriced ? 'text-bad font-semibold' : 'text-foreground'}`}>{eur(lineCost)}</td>
                                            <td className="hidden sm:table-cell px-3 py-2 text-right text-muted-foreground whitespace-nowrap">{pct.toFixed(0)} %</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-muted/30 font-semibold text-foreground">
                                <tr>
                                    <td className="px-3 py-2" colSpan={2}>Coût de la recette{d.portions > 1 ? ` (${d.portions} portions)` : ''}</td>
                                    <td className="px-3 py-2 text-right whitespace-nowrap">{eur(d.total_cost)}</td>
                                    <td className="hidden sm:table-cell px-3 py-2"></td>
                                </tr>
                                {d.portions > 1 && (
                                    <tr className="text-primary">
                                        <td className="px-3 py-2" colSpan={2}>Coût par portion (÷ {d.portions})</td>
                                        <td className="px-3 py-2 text-right whitespace-nowrap">{eur(d.cost_per_portion)}</td>
                                        <td className="hidden sm:table-cell px-3 py-2"></td>
                                    </tr>
                                )}
                            </tfoot>
                        </table>
                    </div>
                ) : (
                    <div className="rounded-lg border border-dashed border-border p-6 text-center">
                        <p className="text-sm text-muted-foreground">
                            Pas encore de recette.{cost > 0 ? ' Le coût affiché vient du prix de revient importé de la caisse, pas d’une recette.' : ' Ajoutez des ingrédients pour calculer le coût et la marge.'}
                        </p>
                    </div>
                )}

                <div className="flex flex-wrap gap-3 pt-5">
                    <button
                        type="button"
                        onClick={onDelete}
                        className="btn-outline sm:hidden !border-bad !text-bad"
                    >
                        <Trash2 className="h-4 w-4" />
                        Supprimer
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-outline flex-1"
                    >
                        Fermer
                    </button>
                    <button
                        type="button"
                        onClick={onEdit}
                        className="btn-primary flex-1"
                    >
                        <Edit className="h-4 w-4" />
                        {d.is_finished_product ? 'Modifier' : hasRecipe ? 'Modifier la recette' : 'Ajouter une recette'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DishSheet;
